import {
  Box,
  InputAdornment,
  OutlinedInput,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import emptyIcon from "../../assets/images/Icons/search-table.png";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useState } from "react";
import { Loader } from "./Loader";
import { Link } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { SortableTableHeader } from "./SortableTableHeader";
import { ScrollTopButton } from "./ScrollTopButton";

export const CustomTable = (props) => {
  const [headerHover, setHeaderHover] = React.useState(null);

  const {
    defaultData,
    columns,
    headerText,
    rightContent,
    subRow = null,
    allRowsExpanded = false,
    customRowStyle = null,
    tableHeaderContent = null,
    isHeaderStyleChange = false,
    isLoading = false,
    isPagination = true,
    isTableHeader = true,
    isTableHead = true,
    tableFooter,
    tableStyle,
    onSearchChange,
    hideEmptyContent = false,
    showSearchBar = true,
    updateTableData,
    emptyTableMessage,
    isHeaderColor,
    onRowLink = false,
    onRowAction = false,
    rowClickNewTab = false,
    manualPagination = false,
    tableHeaderStyles = {},
    searchText = null,
    defaultSort,
    tableId = null,
    manualFiltering = false,
    disablePagination = false,
    customTopRow = null,
    headerContentStyle = {},
    isInTab = false,
    enableTableContainerPaddingBottom = false,
    onChangeRowSelection = null,
    resetRowSelection = false,
    tableMeta = {},
    tableContainerStyle,
    stickyHeader = false,
  } = props;

  // Fetch the previous state from localStorage
  const previousState = tableId
    ? JSON.parse(localStorage.getItem(`table_${tableId}`))
    : null;

  const table = useReactTable({
    data: defaultData,
    columns,
    getSubRows: (row) => row[subRow] || [],
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: onSearchChange,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(disablePagination
      ? {}
      : { getPaginationRowModel: getPaginationRowModel() }),
    getExpandedRowModel: getExpandedRowModel(),
    meta: {
      updateData: updateTableData,
    },
    manualPagination: manualPagination,
    manualFiltering: manualFiltering,
  });
  // Initialize the state from localStorage or use default values
  const [state, setState] = useState({
    ...table.initialState,
    pagination: {
      pageIndex: previousState?.pagination?.pageIndex || 0,
      pageSize: previousState?.pagination?.pageSize || 50,
    },
    sorting: previousState?.sorting ? previousState?.sorting : defaultSort,
    globalFilter: previousState?.globalFilter
      ? previousState?.globalFilter
      : searchText,
  });

  // Update localStorage when state changes
  useEffect(() => {
    if (tableId) {
      localStorage.setItem(`table_${tableId}`, JSON.stringify(state));
    }
  }, [state]);

  // Table state setting function to persist state and localStorage
  table.setOptions((prev) => ({
    ...prev,
    state,
    onStateChange: setState,
    meta: {
      ...prev.meta,
      ...tableMeta,
    },
  }));

  const setGlobalFilter = React.useCallback(
    (value) => {
      setState((prevState) => ({
        ...prevState,
        globalFilter: value,
        // Only update globalFilter without altering pageIndex
        pagination: { ...prevState.pagination },
      }));

      if (onSearchChange) {
        onSearchChange(value);
      }
    },
    [onSearchChange],
  );

  // Handle row expansion
  useEffect(() => {
    if (table && allRowsExpanded) {
      table.toggleAllRowsExpanded(true);
    }
  }, [table, allRowsExpanded]);

  /**
   * Handles mouse hover status over table headers.
   *
   * @param {boolean} status - The hover status to set.
   */
  const handleMouseHover = (status) => setHeaderHover(status);

  // Reset row selection to default
  useEffect(() => {
    if (resetRowSelection)
      setState((prevState) => ({
        ...prevState,
        rowSelection: {},
      }));
  }, [resetRowSelection]);

  // Find the row details and set to callback
  useEffect(() => {
    if (typeof onChangeRowSelection === "function") {
      const selected = state.rowSelection || {};
      const selectedIds = Object.keys(selected).filter((id) => selected[id]);
      const selectedRows = selectedIds
        .map((id) => {
          try {
            const row = table.getRow(id);
            return row ? row.original : null;
          } catch (e) {
            return null;
          }
        })
        .filter(Boolean);
      onChangeRowSelection(selectedRows);
    }
  }, [state.rowSelection]);

  return (
    <Stack
      sx={{
        borderRadius: "4px",
        bgcolor: "#fff",
        overflow: "hidden",
        ...tableStyle,
      }}
    >
      {isTableHeader && (
        <TableHeader
          setGlobalFilter={setGlobalFilter}
          value={searchText ? searchText : state.globalFilter}
          headerText={headerText}
          rightContent={rightContent}
          isHeaderStyleChange={isHeaderStyleChange}
          showSearchBar={showSearchBar}
          tableHeaderStyles={tableHeaderStyles}
          isInTab={isInTab}
        />
      )}

      {tableHeaderContent && (
        <TableHeaderContent
          tableHeaderContent={tableHeaderContent}
          headerContentStyle={headerContentStyle}
        />
      )}

      <TableContainer
        sx={{
          overflow: "auto",
          backgroundColor: "#F5F8FC",
          pb: enableTableContainerPaddingBottom ? "8px" : 0,
          ...tableContainerStyle,
        }}
      >
        <Table
          stickyHeader={stickyHeader}
          sx={{
            mx: isInTab ? "0" : "8px",
            width: isInTab ? "100%" : "calc(100% - 16px)",
          }}
        >
          {isTableHead && (
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const columnMetaData = header.column.columnDef.meta;
                    const canSort = header.column.getCanSort();
                    const headerName = flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    );
                    const sort = header.column.getIsSorted();
                    const isSortActive = sort !== false;
                    return (
                      <TableCell
                        key={header.id}
                        colSpan={header.colSpan}
                        align={columnMetaData?.align || "left"}
                        sx={{
                          padding: "10px 8px",
                          backgroundColor: isHeaderColor
                            ? "#E6EEF7"
                            : "#F5F8FC",
                          ...columnMetaData,
                        }}
                        onMouseEnter={
                          canSort
                            ? () => handleMouseHover(header.id)
                            : undefined
                        }
                        onMouseLeave={
                          canSort ? () => handleMouseHover(null) : undefined
                        }
                      >
                        {header.isPlaceholder ? null : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              justifyContent:
                                header?.column?.columnDef?.align === "right" &&
                                "flex-end",
                              cursor: canSort ? "pointer" : "",
                            }}
                            {...{
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {canSort ? (
                              <SortableTableHeader
                                headerHover={headerHover}
                                headerId={header.id}
                                headerName={headerName}
                                isSortActive={isSortActive}
                                sort={sort}
                              />
                            ) : (
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "12px",
                                  lineHeight: "14px",
                                }}
                              >
                                {headerName}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>
          )}

          <TableBody
            sx={{
              border: isInTab ? 0 : "1px solid",
              borderColor: "divider",
            }}
          >
            {/* Render custom top row if provided */}
            {customTopRow ? (
              <TableRow>
                <TableCell colSpan={table.getAllColumns().length}>
                  {customTopRow}
                </TableCell>
              </TableRow>
            ) : null}

            {isLoading || table.getFilteredRowModel().rows.length === 0 ? (
              isLoading ? (
                <TableRow>
                  <TableCell align="center" colSpan={columns.length}>
                    <Loader />
                  </TableCell>
                </TableRow>
              ) : !hideEmptyContent ? (
                <EmptyComponent emptyTableMessage={emptyTableMessage} />
              ) : (
                <></>
              )
            ) : (
              table.getRowModel().rows.map((row) => {
                const style = customRowStyle ? customRowStyle(row) : {};
                const isCustomRow =
                  row.depth > 0 && row.original.isCustomRow ? true : false;
                const link =
                  onRowLink && (subRow ? row.depth > 0 : true)
                    ? onRowLink(row.original)
                    : false;
                return (
                  <TableRow
                    key={row.id}
                    component={link ? Link : null}
                    to={link ? link : undefined}
                    onClick={() => {
                      onRowAction && onRowAction(row.original);
                    }}
                    target={rowClickNewTab ? "_blank	" : ""}
                    rel="noopener noreferrer"
                    sx={{
                      textDecoration: "none",
                      cursor: link || onRowAction ? "pointer" : "default",
                      "&:hover": {
                        backgroundColor:
                          link || onRowAction
                            ? "rgba(55, 65, 81, 0.04)"
                            : false,
                      },
                    }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const cellMetaData = cell.column.columnDef.meta;
                      return isCustomRow ? (
                        <TableCell
                          key={cell.id}
                          sx={{
                            padding: "8px",
                            // borderBottom: "1px solid #DFE8F2",
                            // backgroundColor: "#F2F7FC",
                            ...cellMetaData,
                            ...style,
                          }}
                          align={
                            cell.column.columnDef.align ||
                            cell.column.id === "actions"
                              ? "right"
                              : "left"
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ) : (
                        <TableCell
                          key={cell.id}
                          sx={{
                            padding: "8px",
                            ...cellMetaData,
                            ...style,
                          }}
                          align={
                            cell.column.columnDef.align ||
                            cell.column.id === "actions"
                              ? "right"
                              : "left"
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {isPagination &&
          !disablePagination &&
          table.getFilteredRowModel().rows.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[8, 10, 20, 30, 40, 50]}
              component="div"
              count={
                !subRow
                  ? table.getFilteredRowModel().flatRows.length
                  : table.getFilteredRowModel().flatRows.length -
                    table.getFilteredRowModel().rows.length
              }
              rowsPerPage={state.pagination.pageSize}
              page={state.pagination.pageIndex}
              onPageChange={(e, page) => {
                setState((prevState) => ({
                  ...prevState,
                  pagination: { ...prevState.pagination, pageIndex: page },
                }));
              }}
              onRowsPerPageChange={(e) => {
                setState((prevState) => ({
                  ...prevState,
                  pagination: {
                    ...prevState.pagination,
                    pageSize: Number(e.target.value),
                  },
                }));
              }}
              sx={{
                bgcolor: "#FFFFFF",
                border: isInTab ? "0" : "1px  solid #DFE8F2",
                borderTop: "none",
                mx: isInTab ? "0" : "8px",
              }}
            />
          )}
        {tableFooter && <>{tableFooter}</>}
      </TableContainer>

      {props?.enableScrollTop && <ScrollTopButton />}
    </Stack>
  );
};

const TableHeader = ({
  setGlobalFilter,
  headerText,
  rightContent,
  isHeaderStyleChange,
  showSearchBar,
  value,
  tableHeaderStyles,
  isInTab,
}) => {
  const { fontWeight, fontSize } = tableHeaderStyles;
  const titleFontWeight = isInTab || fontWeight ? 500 : 600;
  const titleFontSize = isInTab || fontSize ? "18px" : "24px";
  return (
    <Stack
      sx={{
        borderRadius: "4px",
        padding: "16px",
        height: "62px",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        bgcolor: isHeaderStyleChange ? "#111C2B" : "#fff",
        color: isHeaderStyleChange && "#fff",
        ...tableHeaderStyles,
      }}
    >
      <Typography
        sx={{
          fontWeight: titleFontWeight,
          fontSize: titleFontSize,
        }}
      >
        {headerText}
      </Typography>

      {showSearchBar && (
        <OutlinedInput
          onChange={(e) => setGlobalFilter(e.target.value)}
          value={value}
          sx={{
            height: "36px",
            borderRadius: "10px",
            width: "200px",
            backgroundColor: isHeaderStyleChange ? "#313C4C" : "#F2F7FC",
            borderColor: isHeaderStyleChange && "#485364",

            "& input::placeholder": {
              color: isHeaderStyleChange && "#C4CFE4",
            },
            "& input": {
              p: "12.5px 14px 12.5px 4px !important",
              color: isHeaderStyleChange && "#C4CFE4",
            },
            "&:focus-within": {
              width: "400px",
            },
            transition: "width 0.5s",
          }}
          placeholder="Search here..."
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon size="small" />
            </InputAdornment>
          }
          endAdornment={
            value && (
              <InputAdornment
                sx={{ cursor: "pointer" }}
                position="end"
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent default behavior
                  setGlobalFilter("");
                }}
              >
                <CloseIcon size="large" />
              </InputAdornment>
            )
          }
        />
      )}
      {rightContent ? rightContent : <div />}
    </Stack>
  );
};

const EmptyComponent = ({ emptyTableMessage }) => {
  return (
    <TableRow>
      <TableCell
        align="center"
        colSpan={20}
        height={162}
        sx={{ py: 2, padding: "32px 0" }}
      >
        <Stack alignItems="center" gap="8px">
          <img src={emptyIcon} alt="empty" height={56} width={56} />
          <Typography
            sx={{ fontWeight: 400, fontSize: "14px", color: "#485364" }}
          >
            {emptyTableMessage ? emptyTableMessage : "No matches found"}
          </Typography>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const TableHeaderContent = ({
  tableHeaderContent,
  headerContentStyle = {},
}) => {
  return (
    <Box
      sx={{
        p: "8px 16px",
        ...headerContentStyle,
      }}
    >
      {tableHeaderContent}
    </Box>
  );
};
